import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import packageJson from "../../../../package.json";

import { swActions } from "@/store/actions";
import { makeUrlsStaticForUsingAppOffline } from "@/shared/utils/sw/sw";
import { Themes } from "@/shared/utils/theme/theme";
import { isDevelopment } from "@/shared/utils/assembly";
import { NEXT_URLS } from "@/utils/routes/routes.constants";
import { getLocalStorageItem, setLocalStorageItem } from "@/shared/utils/storage/storage";
import { selectorSystemOnLine } from "@/shared/store/system/selectors";
import { DEFAULT_LANGUAGE, I18N_NAMESPACES_LANDING } from "@/shared/utils/multilang/constants";
import { LS_KEY_PWA_CONFIG } from "@/hooks/sw/constants";
import { selectorMainTreeLangMap } from "@/shared/store/mainTree/selectors";
import { ThemesLandingApp } from "@/utils/theme";
import {
  getBookCover,
  getUrlImagesBooks,
  makePublicUrl,
  makeUrlImgAuthor,
  makeUrlImgAuthorSignature,
  makeUrlImgBookCover,
  makeUrlImgBookPaper,
  makeUrlImgBookReflection,
  makeUrlImgBookSplash,
} from "@/shared/utils/url/url";

const makeUrlsImagesBook = (book, imagesSubFolder) => {
  return [
    makeUrlImgBookPaper(book, imagesSubFolder),
    makeUrlImgBookCover(book, imagesSubFolder),
    makeUrlImgBookReflection(book, imagesSubFolder),
    getUrlImagesBooks() + "/book-shadow.png",
  ];
};

const addPrefixBuildFolder = (arr) => arr.map((u) => "/_next/" + u);

const URLS_OFFLINE_COMMON = [
  NEXT_URLS.offline,

  NEXT_URLS.read + "/[id]",
  NEXT_URLS.privacyPolicy,
  NEXT_URLS.copirightNotice,
  NEXT_URLS.userAgreement,
];

const URLS_OFFLINE = URLS_OFFLINE_COMMON;

const URLS_OFFLINE_LANDING = [...URLS_OFFLINE_COMMON, NEXT_URLS.home];

export const useDefineWebsiteUrlsForOfflineUse = (isLanding, pageProps, book) => {
  const dispatch = useDispatch();

  const onLine = useSelector(selectorSystemOnLine);
  const langMap = useSelector(selectorMainTreeLangMap);

  const [pwaConfig, setPwaConfig] = useState({});

  const { reactLoadableManifest, buildManifest } = pwaConfig;
  const isPWAConfigFetched = !!(reactLoadableManifest && buildManifest);

  const langCurrent = pageProps._nextI18Next
    ? Object.keys(pageProps._nextI18Next.initialI18nStore)?.[0] ||
      pageProps._nextI18Next.userConfig.defaultNS
    : DEFAULT_LANGUAGE;

  const bookAuthor = book?.author;
  const bookIdOrigin = book?.book_id;

  const urlsForOfflineUseLocal = useMemo(() => {
    if (!("window" in global) || !isPWAConfigFetched) {
      return [];
    }

    const urls = new Set([
      // Necessary files.
      ...addPrefixBuildFolder(buildManifest.lowPriorityFiles),
      ...addPrefixBuildFolder(buildManifest.polyfillFiles),

      // Static files.
      ...makeUrlsStaticForUsingAppOffline(
        langCurrent,
        isLanding ? ThemesLandingApp : Themes,
        I18N_NAMESPACES_LANDING,
        langMap,
      ),
    ]);

    // Landing page images.
    if (isLanding && bookAuthor && bookIdOrigin) {
      const imagesSubFolder = pageProps.landingBookViewConfig?.imagesSubFolder;

      urls.add(makeUrlImgAuthorSignature(bookAuthor));
      urls.add(makeUrlImgAuthor(bookAuthor));

      urls.add(makePublicUrl("/scripts/qrious.min.js"));
      urls.add(makePublicUrl("/scripts/what-input.min.js"));
      urls.add(makePublicUrl("/scripts/landing.auth.js"));

      if (pageProps.isSetLanding && pageProps.booksSet?.length) {
        pageProps.booksSet.forEach((book) => urls.add(getBookCover(book, true)));
        pageProps.booksSet.forEach((book) => urls.add(getBookCover(book, false)));
      } else {
        makeUrlsImagesBook(book, imagesSubFolder).forEach((i) => urls.add(i));
      }

      if (pageProps.landingBookViewConfig?.splashAsImage) {
        urls.add(makeUrlImgBookSplash(imagesSubFolder));
      }
    }

    // Dynamic import modules (e.g. modules that are imported with "dynamic()").
    if (reactLoadableManifest) {
      Object.values(reactLoadableManifest).forEach(({ files }) => {
        addPrefixBuildFolder(files).forEach((i) => urls.add(i));
      });
    }

    // Dev scripts.
    if (isDevelopment()) {
      urls.add("/_next/static/development/_devPagesManifest.json");
      addPrefixBuildFolder(buildManifest.devFiles).forEach((i) => urls.add(i));
    }

    // Necessary files.
    if (buildManifest.pages["/_app"]) {
      addPrefixBuildFolder(buildManifest.pages["/_app"]).forEach((i) => urls.add(i));
    }

    // Pages scripts.
    const urlsOfflineFinal = isLanding ? URLS_OFFLINE_LANDING : URLS_OFFLINE;
    for (const url of urlsOfflineFinal) {
      if (buildManifest.pages[url]) {
        addPrefixBuildFolder(buildManifest.pages[url]).forEach((i) => urls.add(i));
      }
    }

    // Pages HTMLs.
    const prefixPathnameLocale = "/" + langCurrent;
    urlsOfflineFinal.forEach((u) => {
      urls.add(
        u === "/" ? prefixPathnameLocale : prefixPathnameLocale + u.replace("[id]", "offline"),
      );
    });

    // Pages data.
    const prefixData = "/_next/data/";
    const buildID = buildManifest.lowPriorityFiles[0].split("/")[1];
    urls.add(prefixData + buildID + prefixPathnameLocale + ".json");

    return [...urls.values()];
  }, [
    // Ignore missing dependency {book} so far,
    // it will be triggered anyway by {bookAuthor} or {bookIdOrigin}
    isLanding,
    buildManifest,
    reactLoadableManifest,
    bookAuthor,
    bookIdOrigin,
    langCurrent,
    isPWAConfigFetched,
    langMap,
  ]);

  // Sets urls for offline use to redux.
  useEffect(() => {
    dispatch(swActions.updateWebsiteUrls(urlsForOfflineUseLocal));
  }, [urlsForOfflineUseLocal]);

  // Fetches PWA Config.
  useEffect(() => {
    const loadStorageConfig = () => {
      const storageItem = getLocalStorageItem(LS_KEY_PWA_CONFIG, undefined, true);
      if (storageItem) {
        setPwaConfig(storageItem);
      }
    };

    if (onLine) {
      fetch(NEXT_URLS.apiGetNextPWAConfig + "?v=" + packageJson.version)
        .then(async (response) => {
          const data = await response.json();

          if (data?.buildManifest) {
            const pwaConfigNew = {
              buildManifest: data.buildManifest,
              reactLoadableManifest: data.reactLoadableManifest,
            };
            setPwaConfig(pwaConfigNew);
            setLocalStorageItem(LS_KEY_PWA_CONFIG, pwaConfigNew, true);
          }
        })
        .catch(loadStorageConfig);
    } else {
      loadStorageConfig();
    }
  }, [onLine]);
};
